import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, Plus, Edit3, Trash2 } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppSwitch } from '../components/base/AppSwitch';
import { Toast } from '../components/Toast';
import { PolicyEditModal, PolicyCreateModal, PolicyDeleteModal } from '../components/modals/PolicyModals';
import * as mockApi from '../services/mockApi';
import type { Policy } from '../types';
export function PolicyPage() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  // Modal states
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState<Policy | null>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Load policies
  useEffect(() => {
    loadPolicies();
  }, []);
  const loadPolicies = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getPolicies();
      setPolicies(data);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento delle policy',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleToggle = async (policyId: string) => {
    const policy = policies.find(p => p.id === policyId);
    if (!policy) return;
    try {
      await mockApi.updatePolicy(policyId, {
        enabled: !policy.enabled
      });
      setPolicies(prev => prev.map(p => p.id === policyId ? {
        ...p,
        enabled: !p.enabled
      } : p));
      setToast({
        message: `Policy ${!policy.enabled ? 'attivata' : 'disattivata'}`,
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'aggiornamento della policy",
        type: 'error'
      });
    }
  };
  const handleSaveEdit = async (id: string, data: Partial<Policy>) => {
    try {
      const updated = await mockApi.updatePolicy(id, data);
      setPolicies(prev => prev.map(p => p.id === id ? updated : p));
      setToast({
        message: 'Policy aggiornata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
      throw error;
    }
  };
  const handleCreate = async (data: Omit<Policy, 'id'>) => {
    try {
      const newPolicy = await mockApi.createPolicy(data);
      setPolicies(prev => [...prev, newPolicy]);
      setToast({
        message: 'Policy creata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nella creazione',
        type: 'error'
      });
      throw error;
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await mockApi.deletePolicy(id);
      setPolicies(prev => prev.filter(p => p.id !== id));
      setToast({
        message: 'Policy eliminata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'eliminazione",
        type: 'error'
      });
      throw error;
    }
  };
  const filteredPolicies = selectedCategory === 'all' ? policies : policies.filter(p => p.category === selectedCategory);
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AppBadge variant="rose" data-id="element-1723">Alta</AppBadge>;
      case 'medium':
        return <AppBadge variant="amber" data-id="element-1724">Media</AppBadge>;
      case 'low':
        return <AppBadge variant="gray" data-id="element-1725">Bassa</AppBadge>;
      default:
        return null;
    }
  };
  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      privacy: 'Privacy',
      content: 'Contenuti',
      behavior: 'Comportamento',
      compliance: 'Compliance'
    };
    return labels[category] || category;
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-20" data-id="element-1726">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-1727">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-8" data-id="element-1728">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1729" />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1730">
        <div data-id="element-1731">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-1732">
            Policy
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1733">
            Gestisci le regole di sicurezza e conformità del service
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1734">
        <AppCard className="flex flex-col items-center justify-center py-6" data-id="element-1735">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1736">
            {policies.filter(p => p.enabled).length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide font-medium" data-id="element-1737">
            Policy attive
          </p>
        </AppCard>

        <AppCard className="flex flex-col items-center justify-center py-6" data-id="element-1738">
          <p className="text-2xl font-semibold text-rose-600 dark:text-rose-400" data-id="element-1739">
            {policies.filter(p => p.severity === 'high' && p.enabled).length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide font-medium" data-id="element-1740">
            Alta priorità
          </p>
        </AppCard>

        <AppCard className="flex flex-col items-center justify-center py-6" data-id="element-1741">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1742">
            {policies.filter(p => p.category === 'compliance').length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide font-medium" data-id="element-1743">
            Compliance
          </p>
        </AppCard>

        <AppCard className="flex flex-col items-center justify-center py-6" data-id="element-1744">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1745">
            {policies.filter(p => p.category === 'privacy').length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide font-medium" data-id="element-1746">
            Privacy
          </p>
        </AppCard>
      </div>

      {/* Policies List */}
      <div className="space-y-4" data-id="element-1747">
        {filteredPolicies.map(policy => <AppCard key={policy.id} className={`transition-all duration-200 hover:shadow-md ${!policy.enabled ? 'opacity-75 grayscale-[0.5]' : ''}`} data-id="element-1748">
            <div className="flex items-start justify-between" data-id="element-1749">
              <div className="flex items-start flex-1 mr-6" data-id="element-1750">
                <div className={`p-2.5 rounded-xl mr-5 flex-shrink-0 ${policy.severity === 'high' ? 'bg-rose-50 dark:bg-rose-900/20' : policy.severity === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-100 dark:bg-gray-800'}`} data-id="element-1751">
                  {policy.severity === 'high' ? <AlertTriangle className={`w-5 h-5 ${policy.enabled ? 'text-rose-600 dark:text-rose-400' : 'text-rose-400 dark:text-rose-600'}`} data-id="element-1752" /> : <Shield className={`w-5 h-5 ${policy.enabled ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'}`} data-id="element-1753" />}
                </div>

                <div className="flex-1 min-w-0" data-id="element-1754">
                  <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-1755">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100" data-id="element-1756">
                      {policy.title}
                    </h3>
                    {getSeverityBadge(policy.severity)}
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-dark-subtle text-gray-600 dark:text-gray-400" data-id="element-1757">
                      {getCategoryLabel(policy.category)}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl" data-id="element-1758">
                    {policy.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-1759">
                <AppSwitch checked={policy.enabled} onChange={() => handleToggle(policy.id)} data-id="element-1760" />

                <div className="flex gap-1 border-l border-gray-200 dark:border-dark-subtle pl-4" data-id="element-1761">
                  <button onClick={() => setEditingPolicy(policy)} className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-hover rounded-lg transition-all" data-id="element-1762">
                    <Edit3 className="w-4 h-4" data-id="element-1763" />
                  </button>
                  <button onClick={() => setDeletingPolicy(policy)} className="p-2 text-gray-400 dark:text-gray-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all" data-id="element-1764">
                    <Trash2 className="w-4 h-4" data-id="element-1765" />
                  </button>
                </div>
              </div>
            </div>
          </AppCard>)}
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-8 right-8" data-id="element-1766">
        <button onClick={() => setShowCreateModal(true)} className="flex items-center justify-center w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-900" data-id="element-1767">
          <Plus className="w-6 h-6" data-id="element-1768" />
        </button>
      </div>

      {/* Modals */}
      <PolicyEditModal isOpen={!!editingPolicy} policy={editingPolicy} onClose={() => setEditingPolicy(null)} onSave={handleSaveEdit} data-id="element-1769" />

      <PolicyCreateModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreate={handleCreate} data-id="element-1770" />

      <PolicyDeleteModal isOpen={!!deletingPolicy} policy={deletingPolicy} onClose={() => setDeletingPolicy(null)} onDelete={handleDelete} data-id="element-1771" />
    </div>;
}